/** OrgPilot — hybrid feed + artifact editor layout (composition root). */
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { ErrorBoundary } from "@/components/organisms/ErrorBoundary";
import { useSidebar } from "@ncoder/ui";
import toast from "react-hot-toast";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { AgentFeed } from "./AgentFeed";
import { SidebarMetadataSection } from "./components/SidebarMetadataSection";
import { SidebarRules } from "./components/SidebarRules";
import { useSidebarMetadata } from "./components/useSidebarMetadata";
import { MetadataEditorPanel } from "./components/MetadataEditorPanel";
import { useMetadataEditorState } from "./components/useMetadataEditorState";
import { RightPanels } from "./components/RightPanels";
import { SessionList } from "./components/SessionList";
import { useDevAgentSession } from "./useDevAgentSession";
import { useResizeDrag } from "./useResizeDrag";
import { DevAgentReauthModal } from "./components/DevAgentReauthModal";
import { DevAgentResizeGrip } from "./components/DevAgentResizeGrip";
import { EditorWorkspacePinActions } from "./components/EditorWorkspacePinActions";
import { runDevAgentPostSaveAnalysis } from "./devAgentPostSaveAnalysis";
import { useDevAgentChrome } from "./devAgentPage/useDevAgentChrome";
import { useDevAgentEditorBridge } from "./devAgentPage/useDevAgentEditorBridge";

// ── Main component ──────────────────────────────────────────────────────

export default function DevAgent() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);
  const userId = useAuthStoreV1((s) => s.user?.id);
  const { setOpen: setGlobalSidebarOpen } = useSidebar();
  const { reauthModalOpen, setReauthModalOpen } = useDevAgentChrome({
    orgId,
    userId,
    setGlobalSidebarOpen,
  });

  const { runStatus, sessionUuid, editorOpen, setPendingFile, setEditorOpen } = useDevAgentStore(
    useShallow((s) => ({
      runStatus: s.runStatus,
      sessionUuid: s.sessionUuid,
      editorOpen: s.editorOpen,
      setPendingFile: s.setPendingFile,
      setEditorOpen: s.setEditorOpen,
    }))
  );

  // ── Layout state ────────────────────────────────────────────────────
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const sidebar = useResizeDrag({ min: 180, max: 400, defaultValue: 240 });
  const editorPanel = useResizeDrag({ min: 350, max: 900, defaultValue: 500 });
  const rightPanel = useResizeDrag({ min: 200, max: 500, defaultValue: 288, reverse: true });

  // ── Sidebar metadata ────────────────────────────────────────────────
  const [selectedMetadata, setSelectedMetadata] = useState<{ id: string; name: string } | null>({ id: "ApexClass", name: "ApexClass" });
  const [isQuickOpenOpen, setIsQuickOpenOpen] = useState(false);
  const { metadataTypeOptions, metadataFiles, loadingFiles, loadingMoreFiles, metadataSearchTerm, hasNextPage, handleLoadMore } = useSidebarMetadata(selectedMetadata);

  // ── Editor state ────────────────────────────────────────────────────
  const editor = useMetadataEditorState(setPendingFile);
  const {
    analyzingFile,
    setAnalyzingFile,
    mergedInitialContent,
    handleOpenFileInEditor,
    handleOpenCodeChange,
    handleCloseEditor,
    handleLoadIntoChat,
  } = useDevAgentEditorBridge({
    editor,
    editorOpen,
    setEditorOpen,
    setSidebarCollapsed,
    setPendingFile,
  });

  // ── Session management (hook) ───────────────────────────────────────
  const session = useDevAgentSession({
    setSidebarCollapsed: setSidebarCollapsed as any,
    editor,
    handleOpenFileInEditor,
  });

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <>
    <DevAgentReauthModal
      open={reauthModalOpen}
      onDismiss={() => setReauthModalOpen(false)}
    />
    <div className="flex flex-1 min-h-0 h-full w-full bg-background overflow-hidden overflow-x-hidden">
      {/* Sidebar */}
      {!sidebarCollapsed ? (
        <div
          className="min-w-[180px] max-w-[400px] border-r border-border/50 flex flex-col overflow-hidden shrink-0 min-h-0 h-full"
          style={{ width: sidebar.width }}
        >
          <div className="flex shrink-0 items-center justify-end px-2 py-1 border-b border-border/30 bg-background z-10">
            <button onClick={() => setSidebarCollapsed(true)} className="p-1 rounded hover:bg-muted/50 transition-colors" title="Collapse sidebar (⌘/)">
              <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
          {/* Metadata panel — its own scroll container, takes top chunk */}
          <div className="flex-[2] min-h-0 overflow-y-auto text-sm border-b border-border/30">
            <SidebarMetadataSection
              metadataTypeOptions={metadataTypeOptions} selectedMetadata={selectedMetadata} setSelectedMetadata={setSelectedMetadata}
              metadataFiles={metadataFiles} loadingFiles={loadingFiles} loadingMoreFiles={loadingMoreFiles}
              metadataSearchTerm={metadataSearchTerm} hasNextPage={hasNextPage} isQuickOpenOpen={isQuickOpenOpen}
              setIsQuickOpenOpen={setIsQuickOpenOpen} onLoadMore={handleLoadMore}
              onOpenFileInEditor={(file) => handleOpenFileInEditor({ id: String(file.id), name: file.name, metadataType: file.metadataType })}
              disabled={runStatus === "running"}
            />
          </div>
          {/* Rules panel — separate sibling, own scroll, capped height so it
              doesn't steal space from metadata or sessions. Visually split
              from the metadata section with a border. */}
          <div className="shrink-0 max-h-[30vh] overflow-y-auto text-sm border-b border-border/30 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full">
            <SidebarRules userId={useAuthStoreV1.getState().user?.id || ""} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto border-t border-border/30 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full">
          <SessionList
            sessions={session.sessions} sessionUuid={sessionUuid}
            multiSelectMode={session.multiSelectMode} selectedForDelete={session.selectedForDelete}
            toggleSelectForDelete={session.toggleSelectForDelete} setSelectedForDelete={session.setSelectedForDelete}
            editingSessionId={session.editingSessionId} editingTitle={session.editingTitle}
            setEditingTitle={session.setEditingTitle} setEditingSessionId={(id) => session.startRename(id || "", "")}
            menuOpenId={session.menuOpenId} setMenuOpenId={session.setMenuOpenId}
            confirmDeleteId={session.confirmDeleteId} setConfirmDeleteId={session.setConfirmDeleteId}
            onSwitchSession={session.switchSession} onStartRename={session.startRename} onCommitRename={session.commitRename}
            onDeleteSession={session.handleDeleteSession} onMultiDelete={session.handleMultiDelete}
            onToggleMultiSelect={() => { session.setMultiSelectMode(!session.multiSelectMode); session.setSelectedForDelete(new Set()); }}
            onNewSession={session.handleNewSession}
          />
          </div>
        </div>
      ) : (
        <div className="w-10 border-r border-border/50 flex flex-col items-center pt-2 shrink-0">
          <button onClick={() => setSidebarCollapsed(false)} className="p-1.5 rounded hover:bg-muted/50 transition-colors" title="Expand sidebar">
            <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Sidebar resize handle — visible divider + drag grip on hover */}
      {!sidebarCollapsed && (
        <DevAgentResizeGrip
          onMouseDown={sidebar.onMouseDown}
          title="Drag to resize sidebar"
        />
      )}

      {/* Code Editor */}
      {editorOpen && editor.openFiles.length > 0 && (
        <>
        <ErrorBoundary fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Editor crashed — close and reopen the file</div>}>
        <MetadataEditorPanel
          openFiles={editor.openFiles} activeFileId={editor.activeFileId}
          onActiveFileChange={(id) => id && editor.activateTab(id)}
          onCloseTab={editor.handleCloseTab} onReorderTabs={editor.reorderTabs} panelWidth={editorPanel.width}
          onClose={handleCloseEditor} acceptedCode={editor.acceptedCode}
          onAcceptedCodeChange={(code) => editor.setAcceptedCode(code)} onPendingFileChange={setPendingFile}
          initialContent={mergedInitialContent} orgId={orgId} onLoadIntoChat={handleLoadIntoChat}
          isAnalyzing={!!analyzingFile}
          onCanonicalFileId={(canonicalId, previousId) => {
            editor.migrateFileId(previousId, canonicalId);
          }}
          onFileSaved={async (data) => {
            editor.setInitialContent((prev) => {
              const next = { ...prev };
              delete next[data.id];
              return next;
            });
            const uuid = useDevAgentStore.getState().sessionUuid;
            if (!uuid) return;
            setAnalyzingFile(data.fileName);
            try {
              await runDevAgentPostSaveAnalysis(uuid, data);
            } catch (err) {
              console.warn("[PostSaveAnalysis] failed:", err);
            } finally {
              setAnalyzingFile(null);
            }
          }}
          extraActions={
            <EditorWorkspacePinActions
              activeFile={editor.openFiles.find((f) => f.id === editor.activeFileId)}
              initialContentById={editor.initialContent}
              orgId={orgId}
              busy={runStatus === "running"}
            />
          }
          onRunTest={(className) => {
            toast(`Running ${className}...`);
            useDevAgentStore.getState().runTest(className);
          }}
          isBusy={runStatus === "running"}
        />
        </ErrorBoundary>
        <DevAgentResizeGrip
          onMouseDown={editorPanel.onMouseDown}
          title="Drag to resize editor"
        />
        </>
      )}

      {/* Feed — above resize grips (z-[1]) so header controls stay clickable */}
      <div className="relative z-[2] flex min-h-0 h-full min-w-0 flex-1">
        <AgentFeed
          onSendMessage={session.handleSend}
          onStop={session.handleStop}
          disabled={runStatus === "running"}
          mcpTools={session.mcpTools}
          onNewSession={session.handleNewSession}
          currentSessionTitle={session.sessions.find((s) => s.uuid === sessionUuid)?.title}
          onReconnectSSE={session.reconnectSSE}
        />
      </div>

      {/* Right panel resize handle — visible divider + drag grip on hover */}
      <DevAgentResizeGrip
        onMouseDown={rightPanel.onMouseDown}
        title="Drag to resize right panel"
      />

      {/* Right panels */}
      <RightPanels
        onSendMessage={session.handleSend} onApprove={session.handleApprove} onOpenFileInEditor={handleOpenFileInEditor}
        onOpenCodeChange={handleOpenCodeChange} rightPanelWidth={rightPanel.width} analyzingFile={analyzingFile}
        isBusy={runStatus === "running"} collapsed={rightPanelCollapsed} onToggleCollapse={() => setRightPanelCollapsed((v) => !v)}
        sessionUuid={sessionUuid}
        onDocsPostedToChat={() => {}}
      />
    </div>
    </>
  );
}
